import { uuidModule } from "@/modules/uuid";
import { SpaceAccountNoteAttachmentModelData } from "@/store/note-attachments/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import {
  type IBatchUpdateSpaceAccountNoteAttachmentHostingOperation,
  type NoteAttachmentHostingItem,
} from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncModelKind } from "@/store/sync/types";

/**
 * Writes the FE-driven `hosting_status` lifecycle (PENDING → READY/FAILED) for
 * direct uploads. The BE op accepts the FE-supplied hosting fields per item and
 * persists them to the sync model + NoteAttachment row (no reprocess, no
 * round-trip). Each item also produces an optimistic `UPSERTED` update so the
 * badge updates immediately; `recompute` replays the last optimistic update, so
 * it must carry the full merged model_data, not just the changed fields.
 *
 * `item.id` is the **SpaceAccountNoteAttachment sync-model id** (the row's
 * `model_id`), NOT the raw `associated_note_attachment_id` — the BE filters
 * `SpaceAccountNoteAttachmentSyncModel` by it. Callers convert via
 * `resolveSpaceAccountNoteAttachmentSyncModelUuid` before dispatching (see
 * `writeHostingStatusOptimistically`).
 */
export class BatchUpdateSpaceAccountNoteAttachmentHostingOperation extends BaseSyncOperation<IBatchUpdateSpaceAccountNoteAttachmentHostingOperation> {
  get operationKind() {
    return "BATCH_UPDATE_SPACE_ACCOUNT_NOTE_ATTACHMENT_HOSTING" as const;
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const updates = await Promise.all(
      this.payload.items.map(item => this.buildOptimisticUpdate(item))
    );
    return updates.filter(
      (update): update is OptimisticSyncUpdate<SyncModelData> => update !== null
    );
  }

  async triggerRecompute(): Promise<void> {
    for (const item of this.payload.items) {
      // `item.id` is the sync-model id (== row model_id) — recompute directly.
      await this.store.spaceAccountNoteAttachments.recompute(item.id);
    }
  }

  private async buildOptimisticUpdate(
    item: NoteAttachmentHostingItem
  ): Promise<OptimisticSyncUpdate<SpaceAccountNoteAttachmentModelData> | null> {
    // `item.id` is already the sync-model id (== model_id); do NOT re-derive.
    const modelId = item.id;
    const current = await this.store.spaceAccountNoteAttachments.localTable.get(modelId);
    // No row to patch — the BatchCreate stub hasn't landed yet. Skip silently;
    // the BE write still lands and a later recompute reconciles.
    if (!current) return null;

    const nextModelData: SpaceAccountNoteAttachmentModelData = {
      ...current.model_data,
      hosting_status:
        (item.hosting_status as SpaceAccountNoteAttachmentModelData["hosting_status"]) ?? null,
      ...(item.serving_url !== undefined ? { serving_url: item.serving_url } : {}),
      ...(item.hosting_failure_reason !== undefined
        ? {
            hosting_failure_reason:
              item.hosting_failure_reason as SpaceAccountNoteAttachmentModelData["hosting_failure_reason"],
          }
        : {}),
    };

    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: modelId,
        model_kind: SyncModelKind.SpaceAccountNoteAttachment,
        model_version: current.model_version,
        model_data: nextModelData,
        model_scopes: current.model_scopes,
      },
    };
  }
}
